import { Box, Autocomplete, CircularProgress, Fade } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import SaveIcon from '@mui/icons-material/Save';
import {
  CustomTextField,
  CustomButton,
  HorizontalForm,
  RESPONSIVE_CONFIG,
  ICONS_CONFIG,
} from '../../../common';
import {
  getContractLabel,
  sortContractsByDate,
  getDateByDueStage,
  formatFeeNumbers,
  determineDueStage,
} from '../utils/registerPaymentUtils';

const ContractSelector = ({
  contratoSeleccionado,
  handleContratoChange,
  handleRegistrarClick,
  customAmount,
  handleImporteChange,
  searchTerm,
  setSearchTerm,
  isSearching,
  setIsSearching,
  searchTimeoutRef,
  handleFilter,
  contracts,
  selectedCobroDate,
}) => {
  const sortedContratos =
    contracts && Array.isArray(contracts) ? sortContractsByDate(contracts) : [];

  const currentDueStage = contratoSeleccionado && selectedCobroDate
    ? determineDueStage(selectedCobroDate, contratoSeleccionado) 
    : null;

  const renderContractOption = (props, option) => (
    <li
      {...props}
      style={{
        padding: '10px',
        borderBottom: '1px solid #eee',
        transition: 'background-color 0.2s ease',
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ fontWeight: 'bold', fontSize: '1.1em', color: '#1976d2' }}>
          Contrato: {option.contract_number}
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
          <Box>
            {option.fee_type_name !== 'Cuota Incompleta' &&
            option.fee_type_name !== 'Cuota Completa' ? (
              <span>
                <b>Tipo:</b> {option.fee_type_name}
              </span>
            ) : option.fee_number2 ? (
              <span>
                <b>Cuotas:</b> {option.fee_number1} y {option.fee_number2}
              </span>
            ) : (
              <span>
                <b>Cuota:</b> {option.fee_number1}
              </span>
            )}
          </Box>
          <Box sx={{ ml: 2 }}>
            <span>
              <b>Fecha:</b> {option.month}/{option.year}
            </span>
          </Box>
        </Box>
      </Box>
    </li>
  );

  return (
    <HorizontalForm
      sx={{
        padding: '20px',
        display: 'flex',
        justifyContent: 'space-between',
        transition: 'all 0.3s ease',
        minHeight: '185px',
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', width: '30%' }}>
        <Box sx={{ display: 'flex', gap: 1, width: '150%', alignItems: 'center', marginLeft: '25px' }}>
          <Autocomplete
            options={sortedContratos}
            getOptionLabel={getContractLabel}
            renderOption={renderContractOption}
            style={{ flex: 1 }}
            value={contratoSeleccionado}
            inputValue={searchTerm}
            onInputChange={(event, newInputValue) => {
              setSearchTerm(newInputValue);
            }}
            onChange={handleContratoChange}
            filterOptions={(options) => options}
            freeSolo
            clearOnBlur={false}
            handleHomeEndKeys={false}
            renderInput={(params) => (
              <CustomTextField
                {...params}
                label="Contrato:"
                name="contract_number"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {isSearching && <CircularProgress color="inherit" size={20} />}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
                size="medium"
              />
            )}
          />
          <CustomButton
            variant="contained"
            buttonIcon={SearchIcon}
            onClick={() => {
              if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
              }

              if (!searchTerm.trim()) {
                setIsSearching(false);
                return;
              }

              setIsSearching(true);
              handleFilter({ target: { name: 'contract_number', value: searchTerm } });
            }}
            disabled={!searchTerm.trim() || isSearching}
            sx={{
              fontSize: RESPONSIVE_CONFIG.FIELDS.buttonFontSize,
              padding: RESPONSIVE_CONFIG.FIELDS.buttonPadding,
              marginTop: 1,
            }}
            iconProps={{
              sx: ICONS_CONFIG.PUBLISH,
            }}
          >
            Buscar
          </CustomButton>
        </Box>
      </Box>

      {contratoSeleccionado && (
        <Fade in timeout={600}>
          <Box sx={{ marginLeft: '250px', width: '60%' }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 0.2,
                padding: '12px',
              }}
            >
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Box sx={{ fontWeight: 'bold', minWidth: '120px' }}>NOMBRE:</Box>
                <Box>{contratoSeleccionado.subscribers}</Box>
              </Box>

              <Box sx={{ display: 'flex', gap: 1 }}>
                <Box sx={{ fontWeight: 'bold', minWidth: '120px' }}>Mes:</Box>
                <Box>
                  {contratoSeleccionado.month} - {contratoSeleccionado.year}
                </Box>
              </Box>

              <Box sx={{ display: 'flex', gap: 1 }}>
                <Box sx={{ fontWeight: 'bold', minWidth: '120px' }}>Fecha Venc:</Box>
                <Box>{currentDueStage ? getDateByDueStage(contratoSeleccionado, currentDueStage.dueStage) : 'Selecciona fecha de cobro'}</Box>
              </Box>

              <Box sx={{ display: 'flex', gap: 1 }}>
                <Box sx={{ fontWeight: 'bold', minWidth: '120px' }}>Cuota/s:</Box>
                <Box>{formatFeeNumbers(contratoSeleccionado)}</Box>
              </Box>

              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Box sx={{ fontWeight: 'bold', minWidth: '120px' }}>Importe:</Box>
                <Box>
                  {contratoSeleccionado.fee_type_name === 'De Puntaje' || contratoSeleccionado.fee_type_name === 'Adjudicación' ? (
                    <CustomTextField
                      type="number"
                      value={customAmount}
                      onChange={handleImporteChange}
                      size="small"
                      sx={{
                        width: '120px',
                        '& .MuiOutlinedInput-root': {
                          height: '32px',
                        },
                      }}
                      inputProps={{
                        step: '0.01',
                      }}
                    />
                  ) : (
                    <>
                      {!currentDueStage
                        ? 'Selecciona fecha de cobro'
                        : currentDueStage.dueStage === 'first_due'
                        ? parseFloat(contratoSeleccionado.first_due_amount).toFixed(3)
                        : currentDueStage.dueStage === 'second_due'
                        ? parseFloat(contratoSeleccionado.second_due_amount).toFixed(3)
                        : currentDueStage.dueStage === 'third_due'
                        ? parseFloat(contratoSeleccionado.third_due_amount).toFixed(3)
                        : 'Generar nuevo importe'}
                    </>
                  )}
                </Box>
              </Box>

              <Box sx={{ display: 'flex', gap: 1 }}>
                <Box sx={{ fontWeight: 'bold', minWidth: '120px' }}>Coef.Puntaje:</Box>
                <Box>{contratoSeleccionado.score_coefficent}</Box>
              </Box>
            </Box>
          </Box>
        </Fade>
      )}

      <Box>
        <CustomButton
          variant="contained"
          buttonIcon={SaveIcon}
          onClick={() => handleRegistrarClick(contratoSeleccionado)}
          sx={{
            fontSize: RESPONSIVE_CONFIG.FIELDS.buttonFontSize,
            padding: RESPONSIVE_CONFIG.FIELDS.buttonPadding,
            marginTop: 1,
            marginRight: 2,
          }}
          iconProps={{
            sx: ICONS_CONFIG.PUBLISH,
          }}
        >
          Registrar
        </CustomButton>
      </Box>
    </HorizontalForm>
  );
};

export default ContractSelector;
